// src/client/index.tsx
import React, { useRef, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { roadRows, roadColumns, placeRoad, getBigEntries, getDerivedEntries } from './roadLogic';
import type { RoadEntry, RoadCell } from './roadLogic';

const i18n = {
  zh: {
    title: "TStudio 全球網路邊緣偵測系統",
    subtitle: "實時公網 IP 數據分析與地理位置資訊",
    loading: "正在邊緣網路偵測中...",
    ip_label: "您的公網 IP 位址",
    geo_label: "地理位置資訊",
    ip_tab: "我的 IP",
    road_tab: "百家樂路紙",
    roadmap_title: "百家樂路紙",
    roadmap_hint: "空白單擊紅／雙擊藍",
    banker: "莊",
    player: "閒",
    clear: "清除路紙",
    fullscreen: "進入全屏",
    exitFullscreen: "退出全屏",
    bead: "珠盤路",
    big: "大路",
    bigEye: "大眼仔",
    small: "小路",
    cockroach: "曱甴路",
    roadmap_note: "紅藍只代表路紙記號，不代表下一局結果。",
    faq_t: "關於 IP 位址的常見問題",
    q1: "什麼是公網 IP 位址？",
    a1: "公網 IP（Public IP）是您連接網際網路時，全世界用來識別您網路設備的唯一身分標識。就像您家裡的實體地址一樣。",
    q2: "IPv4 與 IPv6 有什麼不同？",
    a2: "IPv4 使用 32 位元長度，總量約 43 億個已近枯竭；IPv6 採用 128 位元，提供近乎無限的網址數量。",
    road_faq: "百家樂路紙可以預測下一局嗎？",
    road_answer: "不可以。路紙只整理已記錄的莊、閒及和局結果，紅藍記號不代表下一局結果或勝率。"
  },
  en: {
    title: "TStudio Edge Network Core",
    subtitle: "Real-time public IP analytics and geolocation telemetry.",
    loading: "Detecting via Edge Network...",
    ip_label: "YOUR PUBLIC IP ADDRESS",
    geo_label: "GEOLOCATION TELEMETRY",
    ip_tab: "My IP",
    road_tab: "Baccarat Roads",
    roadmap_title: "Baccarat Roadmap",
    roadmap_hint: "Empty: click red / double-click blue",
    banker: "Banker",
    player: "Player",
    clear: "Clear roads",
    fullscreen: "Enter fullscreen",
    exitFullscreen: "Exit fullscreen",
    bead: "Bead Plate",
    big: "Big Road",
    bigEye: "Big Eye Boy",
    small: "Small Road",
    cockroach: "Cockroach Pig",
    roadmap_note: "Road colors are historical markers, not predictions.",
    isp_label: "INTERNET SERVICE PROVIDER (ISP)",
    faq_t: "Frequently Asked Questions",
    q1: "What is a Public IP Address?",
    a1: "A public IP address is a unique identifier assigned to your device when connecting to the internet, allowing servers worldwide to route data back to you.",
    q2: "What is the difference between IPv4 and IPv6?",
    a2: "IPv4 uses 32-bit addresses and is running out, while IPv6 uses 128-bit addresses, offering virtually unlimited IPs to empower the modern web.",
    road_faq: "Can baccarat roads predict the next round?",
    road_answer: "No. Roads only organize recorded Banker, Player, and Tie results; red and blue markers do not predict the next round."
  }
};

function App() {
  const [ipData, setIpData] = useState({ ip: '', country: '', city: '', isp: '' });
  const [lang, setLang] = useState<'zh' | 'en'>('en');
  const [activeTab, setActiveTab] = useState<'ip' | 'road'>('ip');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingColor = useRef<string>('red');
  const pendingInsertIndex = useRef<number | null>(null);

  useEffect(() => {
    const userLang = navigator.language.toLowerCase();
    if (userLang.includes('zh')) setLang('zh');

    fetch('/api/ip')
      .then(res => res.json())
      .then(data => {
        setIpData({
          ip: data.ip || '',
          country: data.country || '',
          city: data.city || '',
          isp: data.isp || data.ISP || ''
        });
      })
      .catch(err => console.error("Fetch IP error:", err));
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const t = i18n[lang];

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }
    await document.documentElement.requestFullscreen();
  };

  const bigEntries = getBigEntries(outcomes);
  const bigEyeEntries = getDerivedEntries(outcomes, 2);
  const smallEntries = getDerivedEntries(outcomes, 3);
  const cockroachEntries = getDerivedEntries(outcomes, 4);
  const roads = {
    big: { label: t.big, entries: bigEntries },
    bigEye: { label: t.bigEye, entries: bigEyeEntries, grid: placeRoad(bigEyeEntries) },
    small: { label: t.small, entries: smallEntries, grid: placeRoad(smallEntries) },
    cockroach: { label: t.cockroach, entries: cockroachEntries, grid: placeRoad(cockroachEntries) }
  };

  const getBlankCellPlacement = (rowIndex: number, columnIndex: number) => {
    const grid = placeRoad(bigEntries);
    if (!outcomes.length) return rowIndex === 0 && columnIndex === 0
      ? { color: 'red', insertIndex: 0 }
      : null;

    let lastSourceIndex = -1;
    let lastColor = 'red';
    let lastRow = 0;
    let lastColumn = 0;
    let runStartSourceIndex = 0;
    grid.forEach((row, currentRow) => row.forEach((entry, currentColumn) => {
      if (entry && entry.sourceIndex > lastSourceIndex) {
        if (entry.sourceIndex > 0 && outcomes[entry.sourceIndex] !== outcomes[entry.sourceIndex - 1]) {
          runStartSourceIndex = entry.sourceIndex;
        }
        lastSourceIndex = entry.sourceIndex;
        lastColor = entry.color;
        lastRow = currentRow;
        lastColumn = currentColumn;
      }
    }));

    if (lastSourceIndex < 0) return null;
    if (rowIndex === lastRow + 1 && columnIndex === lastColumn && rowIndex < roadRows && !grid[rowIndex][columnIndex]) {
      return { color: lastColor, insertIndex: outcomes.length };
    }

    const blockedContinuation = lastRow + 1 >= roadRows || Boolean(grid[lastRow + 1][lastColumn]);
    let continuationColumn = lastColumn + 1;
    while (continuationColumn < roadColumns && grid[lastRow][continuationColumn]) continuationColumn += 1;
    if (blockedContinuation && rowIndex === lastRow && columnIndex === continuationColumn) {
      return { color: lastColor, insertIndex: outcomes.length };
    }

    const runStartPosition = grid.flatMap((row) => row).find((entry) => entry?.sourceIndex === runStartSourceIndex);
    let nextColumn = runStartPosition
      ? grid[0].findIndex((entry) => entry?.sourceIndex === runStartSourceIndex) + 1
      : lastColumn + 1;
    while (nextColumn < roadColumns && grid[0][nextColumn]) nextColumn += 1;
    if (rowIndex === 0 && columnIndex === nextColumn) {
      return { color: lastColor === 'red' ? 'blue' : 'red', insertIndex: outcomes.length };
    }
    return null;
  };

  const handleBigRoadClick = (entry: RoadEntry | null, rowIndex: number, columnIndex: number) => {
    if (!entry) {
      if (clickTimer.current) return;
      const placement = getBlankCellPlacement(rowIndex, columnIndex);
      if (!placement) return;
      pendingColor.current = placement.color;
      pendingInsertIndex.current = placement.insertIndex;
      clickTimer.current = setTimeout(() => {
        const insertIndex = pendingInsertIndex.current ?? outcomes.length;
        setOutcomes((current) => [
          ...current.slice(0, insertIndex),
          pendingColor.current,
          ...current.slice(insertIndex)
        ]);
        clickTimer.current = null;
        pendingInsertIndex.current = null;
      }, 250);
      return;
    }
    setOutcomes((current) => {
      return current.map((color, index) => index === entry.sourceIndex
        ? (color === 'red' ? 'blue' : 'red')
        : color);
    });
  };

  const handleBigRoadDoubleClick = (entry: RoadEntry | null, rowIndex: number, columnIndex: number) => {
    if (entry) return;
    if (clickTimer.current) clearTimeout(clickTimer.current);
    const placement = getBlankCellPlacement(rowIndex, columnIndex);
    if (!placement) return;
    const insertIndex = placement.insertIndex;
    clickTimer.current = null;
    pendingInsertIndex.current = null;
    setOutcomes((current) => [
      ...current.slice(0, insertIndex),
      'blue',
      ...current.slice(insertIndex)
    ]);
  };

  const renderRoad = (label: string, entries: RoadEntry[], interactive = false, variant = 'big', fixedGrid?: RoadCell[][]) => {
    const grid = fixedGrid || placeRoad(entries);
    return (
      <section className={`road-section ${interactive ? 'road-section-main' : ''}`} key={label}>
        <h3>{label}</h3>
        <div className={`road-grid road-grid-${variant}`} style={{ gridTemplateColumns: `repeat(${roadColumns}, minmax(0, 1fr))` }}>
          {grid.flatMap((row, rowIndex) => row.map((entry, columnIndex) => (
            <button
              className={`road-cell road-cell-${variant} ${entry ? entry.color : ''} ${entry && variant !== 'small' ? 'has-marker' : ''} ${interactive && !entry && getBlankCellPlacement(rowIndex, columnIndex) ? 'legal-target' : ''}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...(interactive && !entry && getBlankCellPlacement(rowIndex, columnIndex) ? { boxShadow: 'inset 0 0 0 2px rgba(40, 117, 111, .5)' } : {}) }}
              key={`${label}-${rowIndex}-${columnIndex}`}
              aria-label={entry ? `${label} ${entry.color}` : `${label} empty`}
              onClick={() => interactive && handleBigRoadClick(entry, rowIndex, columnIndex)}
              onDoubleClick={() => interactive && handleBigRoadDoubleClick(entry, rowIndex, columnIndex)}
              type="button"
            >
              {entry && label === t.bead ? (entry.color === 'red' ? '莊' : '閒') : ''}
              {entry && variant !== 'small' && (
                <span style={variant === 'cockroach'
                  ? { display: 'block', width: '72%', margin: 'auto', borderTop: `2px solid ${entry.color === 'red' ? '#d94347' : '#415eb7'}`, transform: 'rotate(-45deg)' }
                  : { display: 'block', width: '72%', aspectRatio: '1', margin: 'auto', boxSizing: 'border-box', border: `2px solid ${entry.color === 'red' ? '#d94347' : '#415eb7'}`, borderRadius: '50%' }}
                aria-hidden="true"
              />
              )}
            </button>
          )))}
        </div>
      </section>
    );
  };

  return (
    <div className="app-shell">
      <aside className={`browser-tabs ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`} aria-label="頁面分頁">
        <div className="tab-brand">TStudio</div>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen((open) => !open)} type="button" aria-label={sidebarOpen ? '收合選單' : '展開選單'}>{sidebarOpen ? '‹' : '›'}</button>
        <button className={`browser-tab ${activeTab === 'ip' ? 'active' : ''}`} onClick={() => setActiveTab('ip')} type="button">
          <span className="tab-icon">◉</span>{t.ip_tab}
        </button>
        <button className={`browser-tab ${activeTab === 'road' ? 'active' : ''}`} onClick={() => setActiveTab('road')} type="button">
          <span className="tab-icon">◇</span>{t.road_tab}
        </button>
      </aside>
      <main className="page-content">
        {activeTab === 'ip' ? (
          <div className="ip-page">
            <header className="page-header"><p className="eyebrow">TSTUDIO / NETWORK</p><h1>{t.title}</h1><p>{t.subtitle}</p></header>
            <section className="ip-panel">
              <p className="panel-label">{t.ip_label}</p>
              <div className="ip-value">{ipData.ip || t.loading}</div>
              {ipData.country && <div className="location-line"><img src={`https://flagcdn.com/w40/${ipData.country.toLowerCase()}.png`} alt="country flag" /><span>{ipData.city ? `${ipData.city}, ${ipData.country}` : ipData.country}</span></div>}
            </section>
            <section className="faq-panel"><h2>{t.faq_t}</h2><h3>{t.q1}</h3><p>{t.a1}</p><h3>{t.q2}</h3><p>{t.a2}</p><h3>{t.road_faq}</h3><p>{t.road_answer}</p></section>
          </div>
        ) : (
          <div className="road-page">
            <header className="road-header"><div className="road-actions"><button className="road-action" onClick={() => setOutcomes([])} type="button" aria-label={t.clear} title={t.clear}>⌫</button><button className="road-action" onClick={toggleFullscreen} type="button" aria-label={isFullscreen ? t.exitFullscreen : t.fullscreen} title={isFullscreen ? t.exitFullscreen : t.fullscreen}>{isFullscreen ? '⛶' : '⛶'}</button></div></header>
            <div className="legend"><span><i className="legend-dot red" />{t.banker}</span><span><i className="legend-dot blue" />{t.player}</span></div>
            <div className="road-stack">
              <div className="road-row">{renderRoad(roads.big.label, roads.big.entries, true, 'big')}</div>
              <div className="road-row road-row-pair">
                {renderRoad(roads.bigEye.label, roads.bigEye.entries, false, 'big-eye', roads.bigEye.grid)}
                {renderRoad(roads.small.label, roads.small.entries, false, 'small', roads.small.grid)}
              </div>
              <div className="road-row">{renderRoad(roads.cockroach.label, roads.cockroach.entries, false, 'cockroach', roads.cockroach.grid)}</div>
            </div>
          </div>
        )}
      </main>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; background: #082d3b; }
        button { font: inherit; }
        .app-shell { min-height: 100vh; display: flex; color: #edf7f4; font-family: 'Avenir Next', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif; background: radial-gradient(circle at 75% 0%, #1e6570 0%, #082d3b 48%, #061d2a 100%); }
        .browser-tabs { position: relative; flex: 0 0 208px; width: 208px; min-width: 0; overflow: hidden; padding: 22px 14px; border-right: 1px solid rgba(215,242,234,.15); background: rgba(3, 26, 37, .62); backdrop-filter: blur(18px); transition: width .2s ease, flex-basis .2s ease; }
        .browser-tabs.sidebar-closed { flex: 0 0 0; width: 0; padding: 0; border-right: 0; overflow: visible; }
        .sidebar-toggle { position: absolute; top: 12px; right: -13px; z-index: 2; width: 26px; height: 26px; padding: 0; color: #d8eee8; border: 1px solid #6a9e9b; border-radius: 50%; background: #0b3b47; cursor: pointer; line-height: 20px; }
        .sidebar-closed .tab-brand { visibility: hidden; }
        .sidebar-closed .sidebar-toggle { position: fixed; top: 12px; left: 12px; right: auto; }
        .sidebar-closed .browser-tab { display: none; }
        .sidebar-closed .browser-tab { justify-content: center; padding-inline: 8px; font-size: 0; }
        .sidebar-closed .tab-icon { font-size: 16px; }
        .tab-brand { padding: 5px 14px 27px; color: #b8d8ce; letter-spacing: .14em; font: 700 12px Arial, sans-serif; }
        .browser-tab { display: flex; align-items: center; width: 100%; gap: 10px; margin: 5px 0; padding: 14px 13px; color: #93b4b2; border: 1px solid transparent; border-radius: 14px; background: transparent; text-align: left; cursor: pointer; transition: .2s ease; }
        .browser-tab:hover { color: #f2fffa; background: rgba(255,255,255,.07); }
        .browser-tab.active { color: #123640; background: #d8eee8; box-shadow: 0 8px 20px rgba(0,0,0,.18); }
        .tab-icon { width: 20px; text-align: center; font-size: 16px; }
        .page-content { flex: 1; min-width: 0; padding: 24px clamp(16px, 3vw, 42px); }
        .ip-page { max-width: 820px; margin: 0 auto; }
        .page-header, .road-header { margin-bottom: 36px; }
        .eyebrow { margin: 0 0 12px; color: #85c9bf; font: 700 11px Arial, sans-serif; letter-spacing: .18em; }
        h1 { margin: 0; color: #f5fffb; font-size: clamp(32px, 5vw, 58px); line-height: 1; }
        .page-header > p:last-child, .road-header p:last-child { margin: 16px 0 0; color: #a6c8c2; font: 15px Arial, sans-serif; }
        .ip-panel { padding: clamp(30px, 6vw, 70px) 24px; border: 1px solid rgba(210,242,234,.28); border-radius: 3px; background: rgba(8, 42, 53, .66); text-align: center; box-shadow: 12px 12px 0 rgba(5, 25, 35, .26); }
        .panel-label { margin: 0 0 18px; color: #82bdb7; font: 700 12px Arial, sans-serif; letter-spacing: .17em; }
        .ip-value { color: #f6fffb; font: 700 clamp(36px, 8vw, 76px) Arial, sans-serif; letter-spacing: .04em; overflow-wrap: anywhere; }
        .location-line { display: flex; justify-content: center; align-items: center; gap: 10px; margin-top: 28px; color: #b9d8d0; font: 16px Arial, sans-serif; }
        .location-line img { width: 28px; height: 20px; object-fit: cover; }
        .faq-panel { margin-top: 28px; padding: 26px 30px; border-top: 1px solid rgba(190,226,217,.25); }
        .faq-panel h2 { margin: 0 0 22px; font-size: 22px; }.faq-panel h3 { margin: 18px 0 5px; color: #8bd0c4; font: 700 15px Arial, sans-serif; }.faq-panel p { margin: 0; color: #a5c2bd; font: 14px/1.7 Arial, sans-serif; }
        .road-page { max-width: 1440px; margin: 0 auto; }.road-header { position: sticky; top: 12px; z-index: 3; display: flex; justify-content: flex-end; min-height: 30px; margin-bottom: 8px; }.road-actions { display: flex; gap: 6px; padding: 4px; border: 1px solid rgba(216,238,232,.22); border-radius: 12px; background: rgba(3,27,37,.68); backdrop-filter: blur(14px); }.road-action { width: 30px; height: 30px; padding: 0; color: #d8eee8; border: 0; border-radius: 8px; background: transparent; cursor: pointer; font: 18px/1 Arial, sans-serif; }.road-action:hover { color: #fff; background: #1e6570; }
        .legend { display: flex; align-items: center; flex-wrap: wrap; gap: 17px; margin-bottom: 22px; color: #c1dcd5; font: 13px Arial, sans-serif; }.legend span { display: flex; align-items: center; gap: 7px; }.legend small { color: #82aaa8; }.legend-dot { width: 12px; height: 12px; display: inline-block; border-radius: 50%; }.legend-dot.red { background: #db4a4f; }.legend-dot.blue { background: #4967c8; }
        .road-stack { display: flex; flex-direction: column; gap: 24px; width: 100%; overflow: visible; padding: 0 0 14px; }.road-row { width: 100%; min-width: 0; }.road-row-pair { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: clamp(18px, 4vw, 38px); padding-top: 4px; }.road-section { width: 100%; min-width: 0; }.road-section-main { padding: 13px; border: 2px solid #d8eee8; background: rgba(3, 27, 37, .42); }.road-section h3 { margin: 0 0 9px 3px; color: #9fc7c1; font: 700 12px Arial, sans-serif; letter-spacing: .12em; }.road-grid { display: grid; grid-template-columns: repeat(30, minmax(0, 1fr)); grid-template-rows: repeat(6, minmax(0, 1fr)); width: 100%; min-width: 0; aspect-ratio: 30 / 6; align-content: stretch; border-left: 1px solid #719092; border-top: 1px solid #719092; }.road-cell { min-width: 0; min-height: 0; padding: 0; border: 0; border-right: 1px solid #719092; border-bottom: 1px solid #719092; background: rgba(241,250,247,.93); cursor: default; touch-action: manipulation; color: #344e55; font: 700 9px Arial, sans-serif; }.road-section-main .road-cell { cursor: pointer; }.road-cell:hover { background: #cde8df; }.road-cell::after { content: ''; display: block; box-sizing: border-box; width: min(72%, 22px); aspect-ratio: 1; margin: auto; border: max(2px, min(3px, 18%)) solid transparent; border-radius: 50%; }.road-cell.red::after { border-color: #d94347; }.road-cell.blue::after { border-color: #415eb7; }.road-cell-small.red, .road-cell-small.blue { color: inherit; }.road-cell-small.red::after { border: 0; background: #d94347; }.road-cell-small.blue::after { border: 0; background: #415eb7; }.road-cell-cockroach.red::after, .road-cell-cockroach.blue::after { width: 72%; height: 0; aspect-ratio: auto; border: 0; border-top: max(2px, min(3px, 18%)) solid currentColor; border-radius: 0; transform: rotate(-45deg); }.road-cell-cockroach.red { color: #d94347; }.road-cell-cockroach.blue { color: #415eb7; }.road-cell.red, .road-cell.blue { text-align: center; }
        :fullscreen .browser-tabs { display: none; }:fullscreen .page-content { padding: 16px 24px; }:fullscreen .road-page { max-width: none; }:fullscreen .road-stack { gap: 14px; }
        @media (max-width: 700px) { .app-shell { display: block; }.browser-tabs { display: flex; width: 100%; padding: 8px 10px; gap: 6px; border-right: 0; border-bottom: 1px solid rgba(215,242,234,.15); }.browser-tabs.sidebar-closed { display: block; width: 0; padding: 0; border-bottom: 0; }.sidebar-toggle { top: 10px; right: 8px; }.browser-tabs.sidebar-closed .sidebar-toggle { top: 10px; right: auto; left: 10px; }.tab-brand { display: none; }.browser-tab { flex: 1; min-height: 46px; margin: 0; justify-content: center; padding: 11px 8px; font-size: 14px; }.page-content { overflow: hidden; padding: 16px 10px; }.ip-panel { padding: 42px 14px; }.ip-value { font-size: clamp(32px, 11vw, 58px); }.road-header { min-height: 30px; margin-bottom: 6px; }.road-action { width: 32px; height: 32px; }.legend { gap: 13px; line-height: 1.5; margin-bottom: 10px; }.road-stack { overflow-x: auto; overscroll-behavior-x: contain; padding-bottom: 18px; }.road-row { width: 840px; min-width: 840px; }.road-row-pair { display: flex; gap: 18px; }.road-section { flex: 0 0 840px; width: 840px; min-width: 840px; }.road-section-main { padding: 8px; }.road-grid { width: 840px; min-width: 840px; grid-template-columns: repeat(30, minmax(28px, 1fr)); grid-template-rows: repeat(6, 28px); aspect-ratio: auto; }.road-cell::after { width: 20px; } }
        .road-cell.has-marker::after { display: none; }
      `}</style>
    </div>
  );
}

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}